import { alpha } from "@mui/material/styles";

// Modern SaaS Dark Theme (Vercel/Linear inspired)
export const modernColors = {
  background: "#050505", // Extremely dark, almost black
  surface: "rgba(255, 255, 255, 0.03)", // Glassmorphic surface
  surfaceHover: "rgba(255, 255, 255, 0.06)",
  border: "rgba(255, 255, 255, 0.08)",
  borderHover: "rgba(255, 255, 255, 0.2)",
  textPrimary: "#ededed", // Soft white
  textSecondary: "#a0a0a0", // Muted grey (High Contrast)
  accent: "#ffffff", // Pure white for highlights
};

// Modern SaaS Light Theme
export const lightColors = {
  background: "#fafafa", // Off-white
  surface: "rgba(255, 255, 255, 0.8)", // Glassmorphic surface
  surfaceHover: "rgba(255, 255, 255, 1)",
  border: "rgba(0, 0, 0, 0.08)",
  borderHover: "rgba(0, 0, 0, 0.2)",
  textPrimary: "#111111", // Nearly black
  textSecondary: "#555555", // Muted dark grey
  accent: "#000000", // Pure black for highlights
};

const getDesignTokens = (mode) => {
  const colors = mode === "dark" ? modernColors : lightColors;
  return {
    palette: {
      mode, 
      primary: {
        main: colors.accent,
        contrastText: mode === "dark" ? "#000" : "#fff",
      },
      secondary: {
        main: colors.textSecondary,
        contrastText: mode === "dark" ? "#fff" : "#000",
      },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary,
      },
      divider: colors.border,
    },
    shape: {
      borderRadius: 16, // The Bento Box signature curve
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: {
        fontSize: 80,
        fontWeight: 600,
        lineHeight: 1.1,
        letterSpacing: "-0.04em",
      },
      h2: {
        fontSize: 48,
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: "-0.03em",
      },
      h3: {
        fontSize: 32,
        fontWeight: 500,
        letterSpacing: "-0.02em",
      },
      h4: {
        fontSize: 24,
        fontWeight: 500,
        letterSpacing: "-0.01em",
      },
      h5: {
        fontSize: 20,
        fontWeight: 500,
      },
      h6: {
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: colors.textSecondary,
      },
      body1: {
        fontSize: 16,
        lineHeight: 1.6,
        fontWeight: 400,
        color: colors.textSecondary,
      },
      body2: {
        fontSize: 14,
        lineHeight: 1.5,
        fontWeight: 400,
        color: colors.textSecondary,
      },
      button: {
        fontSize: 14,
        fontWeight: 500,
        textTransform: "none", // Modern buttons are not uppercase
      },
    },
  };
};

export default function Theme(mode) {
  const colors = mode === "dark" ? modernColors : lightColors;
  return {
    ...getDesignTokens(mode),
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background,
            backgroundImage: mode === "dark" 
              ? "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0) 50%)" 
              : "radial-gradient(circle at 50% 0%, rgba(0,0,0,0.02) 0%, rgba(255,255,255,0) 50%)",
            backgroundAttachment: "fixed",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            "&::after": {
              display: "none" 
            },
            "::selection": {
              backgroundColor: alpha(colors.accent, 0.2),
              color: colors.accent,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "99px", // Pill shaped buttons
            padding: "8px 24px",
            transition: "all 0.2s ease",
          },
          contained: {
            backgroundColor: colors.textPrimary,
            color: colors.background,
            boxShadow: mode === "dark" ? "0 0 0 1px rgba(255,255,255,0.1)" : "0 0 0 1px rgba(0,0,0,0.05)",
            "&:hover": {
              backgroundColor: mode === "dark" ? "#ffffff" : "#000000",
              boxShadow: mode === "dark" ? "0 0 20px rgba(255,255,255,0.2)" : "0 0 20px rgba(0,0,0,0.1)",
            },
          },
          outlined: {
            borderColor: colors.border,
            color: colors.textPrimary,
            backgroundColor: colors.surface,
            backdropFilter: "blur(10px)",
            "&:hover": {
              backgroundColor: colors.surfaceHover,
              borderColor: colors.borderHover,
            },
          },
          text: {
            color: colors.textSecondary,
            "&:hover": {
              color: colors.textPrimary,
              backgroundColor: colors.surface,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surface,
            backdropFilter: "blur(20px)",
            borderRadius: "24px", // Bigger radius for bento cards
            border: `1px solid ${colors.border}`,
            boxShadow: mode === "dark" ? "0 4px 30px rgba(0, 0, 0, 0.5)" : "0 4px 30px rgba(0, 0, 0, 0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: colors.borderHover,
              backgroundColor: colors.surfaceHover,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: colors.surface,
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            border: `1px solid ${colors.border}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === "dark" ? "#0a0a0a" : "#ffffff", // Solid bg for modals
            border: `1px solid ${colors.border}`,
            boxShadow: mode === "dark" ? "0 20px 60px rgba(0,0,0,0.8)" : "0 20px 60px rgba(0,0,0,0.15)",
            borderRadius: "24px",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            color: colors.textPrimary,
            borderRadius: "8px", // Small radius for chips
            fontSize: "0.85rem",
            backdropFilter: "blur(10px)",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: colors.surfaceHover,
              borderColor: colors.borderHover,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: colors.textSecondary,
            border: `1px solid transparent`,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: colors.surface,
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.border,
          }
        }
      }
    },
  };
}
